import React, { useState, useEffect, useRef, useCallback } from 'react'; // Added useCallback
import {
  CheckCircle,
  XCircle,
  TrendingUp,
  BarChart3,
  Eye,
  Megaphone,
  Users,
  Target,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import { apiGetVideoABCDSAssessment } from 'services/apiService';

const categoryIcons: {[key: string]: any} = {
  'ATTRACT': Eye,
  'BRAND': Megaphone,
  'CONNECT': Users,
  'DIRECT': Target,
  'NONE': BarChart3
};

interface FeatureAnalysisDashboardProps {
  video: IVideo | undefined;
  isParentLoading: boolean;
  onGoBack: () => void;
}

const FeatureAnalysisDashboard: React.FC<FeatureAnalysisDashboardProps> = ({ video, isParentLoading, onGoBack }) => {
  const [data, setData] = useState<FeatureAnalysisReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  // 2. Moved fetchData logic into a useCallback hook
  const fetchData = useCallback(async () => {
    if (!video?.video_id || !video?.project_id || !video?.thread_id) return;

    // 3. Set loading state and clear old data on every fetch
    setIsLoading(true);
    setData(null);

    try {
      const response = await apiGetVideoABCDSAssessment(video.project_id, video.thread_id, video.video_id);
      setData(response);
    } catch (error) {
      console.error('Failed to load feature analysis:', error);
      setData(null); // Ensure data is null on error
    } finally {
      setIsLoading(false);
    }
  }, [video?.video_id, video?.project_id, video?.thread_id]); // Dependencies for useCallback

  // 4. Updated useEffect to use the memoized fetchData
  useEffect(() => {
    if (!isParentLoading) {
      fetchData();
    }
  }, [isParentLoading, fetchData]);

  if (isLoading && video) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4" style={{ width: '70vw' }}>
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
          <div className="text-white text-xl">Loading feature analysis...</div>
        </div>
      </div>
    );
  }

  if (!video && isParentLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4" style={{ width: '70vw' }}>
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
          <div className="text-white text-xl">Loading video...</div>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center" style={{ width: '70vw' }}>
          <div className="text-red-400 text-xl"  style={{ width: '70vw' }}>Video not found.</div>
          <button
            onClick={onGoBack}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-300 flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // 5. Updated the !data error block with a "Retry" button
  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center" style={{ width: '70vw' }}>
          <div className="text-red-400 text-xl">Failed to load feature analysis</div>
          <button
            onClick={onGoBack}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-300 flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>
          You have to wait a few minutes for the analysis to be ready.
        </div>
      </div>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 70) return 'from-green-600 to-green-500';
    if (score >= 40) return 'from-yellow-600 to-yellow-500';
    return 'from-red-600 to-red-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Video Feature Analysis
            </h1>
            <p className="text-gray-400">
              Last updated: {new Date(data.updated_at).toLocaleString()}
            </p>
          </div>
          <button
            onClick={onGoBack}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-300 flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>
        </div>

        {/* Two Column Layout: Video (left) + Content (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Sticky Video Player */}
          {video.output_url && (
            <div className="lg:col-span-4">
              <div className="sticky top-20 bg-gray-800/50 border border-gray-700 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Video Preview</h2>
                <div className="relative bg-black rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    className="w-full aspect-video"
                    src={video.output_url}
                    controls
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          )}

          {/* Right Column - Scrollable Content */}
          <div className={video.output_url ? "lg:col-span-8" : "lg:col-span-12"}>
            {/* Category Score Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {data.features.map((feature, idx) => {
                const totalFeatures = Object.values(feature.sub_categories)
                  .flat()
                  .length;
                const detectedFeatures = Object.values(feature.sub_categories)
                  .flat()
                  .filter(f => f.detected).length;

                return (
                  <div
                    key={idx}
                    className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 hover:border-blue-500/50 transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-semibold mb-1">
                          {feature.feature_category.replace(/_/g, ' ')}
                        </h3>
                        <p className="text-sm text-gray-400">
                          {detectedFeatures} / {totalFeatures} features detected
                        </p>
                      </div>
                      <div className={`text-3xl font-bold ${getScoreColor(feature.category_score)}`}>
                        {feature.category_score.toFixed(1)}%
                      </div>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r ${getScoreBgColor(feature.category_score)} transition-all duration-500`}
                        style={{ width: `${feature.category_score}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Detailed Feature Analysis */}
            {data.features.map((feature, categoryIdx) => (
              <div
                key={categoryIdx}
                className="mb-6 transition-all duration-300"
              >
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                    <TrendingUp className="w-6 h-6 text-blue-400" />
                    {feature.feature_category.replace(/_/g, ' ')}
                  </h2>

                  {Object.entries(feature.sub_categories).map(([subCatName, features]) => {
                    const IconComponent = categoryIcons[subCatName] || BarChart3;
                    
                    return (
                      <div key={subCatName} className="mb-8 last:mb-0">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-cyan-400">
                          <IconComponent className="w-5 h-5" />
                          {subCatName}
                        </h3>
                        
                        <div className="space-y-4">
                          {features.map((feat, idx) => (
                            <div
                              key={idx}
                              className={`bg-gray-900 border ${
                                feat.detected ? 'border-green-500/30' : 'border-gray-700'
                              } rounded-lg p-4 hover:border-blue-500/50 transition-all duration-300`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="mt-1">
                                  {feat.detected ? (
                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                  ) : (
                                    <XCircle className="w-5 h-5 text-gray-500" />
                                  )}
                                </div>
                                <div className="flex-grow">
                                  <div className="flex items-start justify-between mb-2">
                                    <h4 className="font-semibold text-white">
                                      {feat.feature_name}
                                    </h4>
                                    <span
                                      className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                        feat.detected
                                          ? 'bg-green-500/20 text-green-400'
                                          : 'bg-gray-700 text-gray-400'
                                      }`}
                                    >
                                      {feat.detected ? 'Detected' : 'Not Detected'}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-400 mb-3">
                                    {feat.feature_evaluation_criteria}
                                  </p>
                                  {feat.rationale && (
                                    <div className="bg-gray-800 border border-gray-700 rounded-md p-3 mt-2">
                                      <p className="text-sm text-gray-300 italic">
                                        {feat.rationale}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureAnalysisDashboard;
